import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import "./ChatIcon.css";

const FAB_SIZE = 56;
const EDGE_MARGIN = 12; // gap from screen edge when snapped
const DRAG_THRESHOLD = 5; // px movement before considered a drag
const STORAGE_KEY = "chat-fab-pos";

function useDraggable() {
  const getDefault = () => ({
    x: window.innerWidth - FAB_SIZE - EDGE_MARGIN,
    y: window.innerHeight - FAB_SIZE - 96,
  });

  const [pos, setPos] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const p = JSON.parse(saved);
        return {
          x: Math.max(0, Math.min(window.innerWidth - FAB_SIZE, p.x)),
          y: Math.max(0, Math.min(window.innerHeight - FAB_SIZE, p.y)),
        };
      }
    } catch {}
    return getDefault();
  });

  const [snapping, setSnapping] = useState(false);
  const posRef = useRef(pos);
  posRef.current = pos;

  const dragging = useRef(false);
  const startOffset = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);

  const onPointerDown = (e) => {
    dragging.current = true;
    hasMoved.current = false;
    setSnapping(false);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    startOffset.current = {
      x: clientX - posRef.current.x,
      y: clientY - posRef.current.y,
    };
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const newX = Math.max(
        0,
        Math.min(window.innerWidth - FAB_SIZE, clientX - startOffset.current.x),
      );
      const newY = Math.max(
        0,
        Math.min(
          window.innerHeight - FAB_SIZE,
          clientY - startOffset.current.y,
        ),
      );
      if (
        Math.abs(newX - posRef.current.x) > DRAG_THRESHOLD ||
        Math.abs(newY - posRef.current.y) > DRAG_THRESHOLD
      ) {
        hasMoved.current = true;
      }
      setPos({ x: newX, y: newY });
    };

    const onUp = () => {
      if (!dragging.current) return;
      dragging.current = false;
      // Snap to nearest horizontal edge
      const centerX = posRef.current.x + FAB_SIZE / 2;
      const snapX =
        centerX < window.innerWidth / 2
          ? EDGE_MARGIN
          : window.innerWidth - FAB_SIZE - EDGE_MARGIN;
      const snapped = { x: snapX, y: posRef.current.y };
      setSnapping(true);
      setPos(snapped);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(snapped));
      } catch {}
      setTimeout(() => setSnapping(false), 280);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  const fabStyle = {
    left: pos.x,
    top: pos.y,
    right: "auto",
    bottom: "auto",
    transition: snapping ? "left 0.25s cubic-bezier(0.25, 1, 0.5, 1)" : "none",
  };

  const wasDragged = () => hasMoved.current;

  return { fabStyle, onPointerDown, wasDragged };
}

function ChatIcon({ onClick, isChatWindowOpen }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";
  const isLiveChatPage = location.pathname === "/live-chat";

  const [messageIndex, setMessageIndex] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [hasClicked, setHasClicked] = useState(false);

  const { fabStyle, onPointerDown, wasDragged } = useDraggable();

  useEffect(() => {
    if (isLiveChatPage) return; // no popup timers on chat page
    let firstMessageTimer;
    let secondMessageTimer;
    let hidePopupTimer;

    if (!hasClicked) {
      if (isHomePage) {
        firstMessageTimer = setTimeout(() => {
          if (!isChatWindowOpen) {
            setShowPopup(true);
            setMessageIndex(1);
            setMessageCount((c) => c + 1);
          }
        }, 7000);
        secondMessageTimer = setTimeout(() => {
          if (!isChatWindowOpen) {
            setMessageIndex(2);
            setMessageCount((c) => c + 1);
          }
        }, 14000);
        hidePopupTimer = setTimeout(() => {
          if (!isChatWindowOpen) {
            setShowPopup(false);
            setMessageCount(0);
            setMessageIndex(0);
          }
        }, 18000);
      } else {
        firstMessageTimer = setTimeout(() => {
          if (!isChatWindowOpen) {
            setShowPopup(true);
            setMessageIndex(1);
            setMessageCount((c) => c + 1);
          }
        }, 8000);
        hidePopupTimer = setTimeout(() => {
          if (!isChatWindowOpen) {
            setShowPopup(false);
            setMessageCount(0);
            setMessageIndex(0);
          }
        }, 16000);
      }
    }

    return () => {
      clearTimeout(firstMessageTimer);
      clearTimeout(secondMessageTimer);
      clearTimeout(hidePopupTimer);
    };
  }, [isHomePage, isLiveChatPage, isChatWindowOpen, hasClicked]);

  const getMessageText = () => {
    if (isHomePage) {
      if (messageIndex === 1) return "Welcome to TrendBay DS!";
      if (messageIndex === 2) return "Have any questions?";
    } else {
      if (messageIndex === 1) return "Hi there! Need assistance?";
    }
    return null;
  };

  const handleChatClick = (e) => {
    if (wasDragged()) return;
    e.stopPropagation();
    if (window.innerWidth <= 960) {
      navigate("/live-chat");
      return;
    }
    setHasClicked(true);
    setMessageCount(0);
    setMessageIndex(0);
    setShowPopup(false);
    onClick();
  };

  const handleBackClick = () => {
    if (wasDragged()) return;
    navigate(-1);
  };

  // On live-chat page - show draggable back button
  if (isLiveChatPage) {
    return (
      <button
        className="chat-back-fab"
        style={fabStyle}
        onMouseDown={onPointerDown}
        onTouchStart={onPointerDown}
        onClick={handleBackClick}
        aria-label="Go back"
      >
        <ArrowLeft size={22} />
      </button>
    );
  }

  // All other pages - show draggable chat FAB
  return (
    <div className="chat-icon-container">
      {!isChatWindowOpen && showPopup && !hasClicked && (
        <div className="chatpopnot">{getMessageText()}</div>
      )}
      <div
        className={`chat-icon ${isChatWindowOpen ? "active" : ""}`}
        style={fabStyle}
        onMouseDown={onPointerDown}
        onTouchStart={onPointerDown}
        onClick={handleChatClick}
      >
        <img
          src="data:image/png;base64,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"
          alt="trendbay support"
        />
        {!isChatWindowOpen && messageCount > 0 && (
          <span className="message-count">{messageCount}</span>
        )}
      </div>
    </div>
  );
}

export default ChatIcon;
